/**
 * Прокрутка главной для «Недавно добавленных» (ТЗ главной § 8, § 22.5, § 22.7). Прокручивается не окно,
 * а `main` приложения (`Application.tsx`); снизу его закрывает липкая полоса создания, поэтому «видимая
 * область» — от верха `main` до верха полосы.
 */

/** Прокручиваемый контейнер главной. */
export function scrollRoot(from: Element | null | undefined): HTMLElement | null {
  const own = from?.closest('main');
  if (own instanceof HTMLElement) return own;
  const main = typeof document !== 'undefined' ? document.querySelector('main') : null;
  return main instanceof HTMLElement ? main : null;
}

/** Полоса создания сверху страницы (2026-10-28): липкая, закрывает верх видимой области на свою высоту. */
function topDock(root: HTMLElement): HTMLElement | null {
  const dock = root.querySelector('[data-dock][data-dock-placement="top"]');
  return dock instanceof HTMLElement ? dock : null;
}

/** Сколько сверху `main` закрыто строкой создания (0 — строка внизу). */
export function topDockHeight(root: HTMLElement): number {
  return topDock(root)?.offsetHeight ?? 0;
}

/** Поле сверху при прокрутке к записи: `scroll-margin-top` из CSS (компьютер 24, телефон 72 px). */
export function scrollMargin(el: Element, fallback = 24): number {
  const value = parseFloat(getComputedStyle(el).scrollMarginTop);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * Одна прокрутка `main`, чтобы элемент встал наверх видимой области. `top` считается вручную и не больше
 * предела прокрутки — уже после раскрытия записи, иначе у конца страницы заголовок не доедет (§ 8).
 * Возвращает, куда едет `main`, или `null`, если элемент уже на месте — прокрутки и `scrollend` не будет.
 */
export function scrollToTop(root: HTMLElement, el: Element, reduced: boolean): number | null {
  const offset = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
  // Строка создания сверху — элемент встаёт под неё.
  const top = Math.max(0, Math.min(offset - scrollMargin(el) - topDockHeight(root), root.scrollHeight - root.clientHeight));
  const moving = Math.abs(top - root.scrollTop) > 1;
  root.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });
  return moving ? top : null;
}

/**
 * Плавная прокрутка `main` к `target` доехала (§ 8, «Нажатие» п. 4): событие `scrollend` или `scroll`, на котором
 * `main` уже у цели; где `scrollend` нет — ещё 150 мс без событий `scroll` (ученик перехватил прокрутку); не дольше
 * `maxMs`. Не по таймеру от нажатия: длительность плавной прокрутки браузер берёт по расстоянию (на ~1300 px она
 * длиннее 600 мс подсветки, и запись доезжала, когда та уже гасла — приёмка A2-08), а при редких кадрах прокрутка
 * может и не начаться за сотни миллисекунд. Возвращает отмену.
 */
export function onScrollSettled(root: HTMLElement, target: number, done: () => void, maxMs = 3000): () => void {
  const hasScrollEnd = 'onscrollend' in window;
  let finished = false;
  let quietTimer = 0;
  let capTimer = 0;
  const cleanup = () => {
    root.removeEventListener('scrollend', finish);
    root.removeEventListener('scroll', onScroll);
    window.clearTimeout(quietTimer);
    window.clearTimeout(capTimer);
  };
  function finish() {
    if (finished) return;
    finished = true;
    cleanup();
    done();
  }
  function onScroll() {
    if (Math.abs(root.scrollTop - target) <= 2) {
      finish();
      return;
    }
    if (hasScrollEnd) return;
    window.clearTimeout(quietTimer);
    quietTimer = window.setTimeout(finish, 150);
  }
  if (hasScrollEnd) root.addEventListener('scrollend', finish);
  root.addEventListener('scroll', onScroll, { passive: true });
  capTimer = window.setTimeout(finish, maxMs);
  return () => {
    finished = true;
    cleanup();
  };
}

/** Верх элемента за верхним краем видимой области (с учётом поля под гамбургер на телефоне). */
export function isAboveView(root: HTMLElement, el: Element): boolean {
  return el.getBoundingClientRect().top < root.getBoundingClientRect().top + scrollMargin(el) + topDockHeight(root) - 1;
}

/** Сколько видно под элементом до верха липкой полосы (строка сверху — до низа `main`). */
export function spaceBelow(root: HTMLElement, el: Element, dockHeight: number): number {
  const covered = topDock(root) ? 0 : dockHeight;
  return root.getBoundingClientRect().bottom - covered - el.getBoundingClientRect().bottom;
}

/** Плавная прокрутка `main` длится около 300 мс — сворачивать после неё (§ 13). */
export const SCROLL_SETTLE_MS = 320;
